"use client";

import { useState } from "react";

import { WidgetOptionButton } from "./widget-option-button";
import { EMBED_FRAME_BORDER } from "./embed-snippet-style";
import { embedAttributionHtml } from "./embed-attribution";
import { POPULAR_CITIES } from "./popular-cities";
import type { WidgetCatalogEntry } from "./widget-catalog";

/**
 * The one customizer every embeddable widget uses.
 *
 * The panchang, choghadiya and kundali customizers were three copies of this
 * component differing only in embed path, iframe title, height and whether a
 * city picker showed. That duplication is what let a single bad attribution
 * anchor ship in all three at once (2026-08-21). The snippet is now produced in
 * exactly one place, so its link policy cannot drift per widget.
 */
export default function WidgetCustomizer({ widget }: { widget: WidgetCatalogEntry }) {
  const [city, setCity] = useState("new-delhi");
  const [lang, setLang] = useState<"en" | "hi">("en");
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [copied, setCopied] = useState(false);

  const query = widget.withCity
    ? `city=${city}&lang=${lang}&theme=${theme}`
    : `lang=${lang}&theme=${theme}`;
  const previewUrl = `${widget.embedPath}?${query}`;
  const embedCode = `<!-- ${widget.snippetComment} -->
<iframe
  src="https://ishvaram.com${widget.embedPath}?${query}"
  width="100%"
  height="${widget.height}"
  style="border: 1px solid ${EMBED_FRAME_BORDER}; border-radius: 12px; max-width: 480px; width: 100%; display: block;"
  frameborder="0"
  loading="lazy"
  title="${widget.iframeTitle}"
></iframe>
${embedAttributionHtml(widget.dataLabel)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard unavailable (insecure context, permissions) — the code stays
      // selectable in the textarea, so the flow degrades rather than breaking.
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12 items-start">
      <div className="lg:col-span-6 rounded-2xl border border-border-default bg-surface-raised p-6 shadow-sm">
        <h2 className="text-subtitle font-serif font-bold text-text-primary mb-4">
          Customize Your Widget
        </h2>

        <div className="space-y-4 text-body-small">
          {widget.withCity ? (
            <div>
              <label className="block font-medium text-text-primary mb-1.5">
                Select City / Location
              </label>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full rounded-lg border border-border-default bg-surface-canvas px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                {POPULAR_CITIES.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {lang === "hi" ? `${c.nameHi} (${c.name})` : c.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div>
            <label className="block font-medium text-text-primary mb-1.5">Language</label>
            <div className="grid grid-cols-2 gap-2">
              <WidgetOptionButton active={lang === "en"} onClick={() => setLang("en")}>
                English
              </WidgetOptionButton>
              <WidgetOptionButton active={lang === "hi"} onClick={() => setLang("hi")}>
                हिंदी (Hindi)
              </WidgetOptionButton>
            </div>
          </div>

          <div>
            <label className="block font-medium text-text-primary mb-1.5">Color Theme</label>
            <div className="grid grid-cols-2 gap-2">
              <WidgetOptionButton active={theme === "light"} onClick={() => setTheme("light")}>
                ☀️ Light Mode
              </WidgetOptionButton>
              <WidgetOptionButton active={theme === "dark"} onClick={() => setTheme("dark")}>
                🌙 Dark Mode
              </WidgetOptionButton>
            </div>
          </div>

          <div className="pt-2">
            <div className="flex items-center justify-between mb-1.5">
              <label className="font-medium text-text-primary">HTML Embed Code</label>
              <button
                type="button"
                onClick={handleCopy}
                className="text-xs font-semibold text-intent-brand hover:underline"
              >
                {copied ? "✓ Copied to Clipboard!" : "Copy Code"}
              </button>
            </div>
            <textarea
              readOnly
              value={embedCode}
              rows={6}
              className="w-full font-mono text-caption rounded-lg border border-border-default bg-foreground text-background p-3 leading-relaxed focus:outline-none"
            />
            <p className="mt-1.5 text-caption text-text-muted">
              Paste this HTML into any WordPress, Webflow, Ghost, or static site. The credit line is
              optional — the widget keeps working if you remove it.
            </p>
          </div>
        </div>
      </div>

      <div className="lg:col-span-6 flex flex-col items-center">
        <div className="w-full max-w-[480px]">
          <div className="flex items-center justify-between mb-2 px-1 text-xs text-text-muted">
            <span className="font-medium">Live Widget Preview</span>
            <span>480 × {widget.height} px</span>
          </div>
          <div className="rounded-2xl border border-border-default bg-surface-raised p-3 shadow-md">
            <iframe
              key={previewUrl}
              src={previewUrl}
              width={480}
              height={widget.height}
              className="w-full rounded-xl border-0"
              title={`${widget.iframeTitle} preview`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
